import { useFormikEnhanced } from '@superdispatch/forms';
import {
  FormikDrawerActions,
  FormikDrawerContent,
} from 'shared/form/FormikDrawer';
import { useInitialValues } from 'shared/form/AppFormik';
import { useEffect } from 'react';
import { bankAccountValidationSchema } from '../bank-account/BankAccountForm';
import { ChangeBankAccountForm } from './ChangeBankAccountForm';
import {
  ChangeBankAccountStepState,
  SubmittedBankAccount,
  submittedBankAccountSchema,
} from './ChangeBankAccountTypes';

interface UseChangeBankAccountBankInfoFormProps {
  onContinue: (bankAccount: SubmittedBankAccount) => void;
}

export function useChangeBankAccountBankInfoForm({
  onContinue,
}: UseChangeBankAccountBankInfoFormProps) {
  const initialValues = useInitialValues(bankAccountValidationSchema);

  return useFormikEnhanced<typeof initialValues, SubmittedBankAccount>({
    initialValues,
    validationSchema: bankAccountValidationSchema,
    onSubmit: (values) => submittedBankAccountSchema.cast(values),
    onSubmitSuccess: (bankAccount) => {
      onContinue(bankAccount);
    },
  });
}

interface Props {
  onClose: () => void;
  onContinue: (bankAccount: SubmittedBankAccount) => void;
  onStepStateChange: (state: ChangeBankAccountStepState) => void;
}

export function ChangeBankAccountBankInfoStep({
  onClose,
  onContinue,
  onStepStateChange,
}: Props) {
  const formik = useChangeBankAccountBankInfoForm({ onContinue });

  useEffect(() => {
    onStepStateChange({
      isSubmitting: formik.isSubmitting,
      hasUnsavedChanges: formik.dirty && formik.status.type !== 'submitted',
    });
  }, [
    formik.dirty,
    formik.isSubmitting,
    formik.status.type,
    onStepStateChange,
  ]);

  return (
    <FormikDrawerContent
      formik={formik}
      title="Change Bank Account"
      onClose={onClose}
      disableNavigationBlock={true}
      disableAutoClose={true}
      actions={<FormikDrawerActions submitButtonLabel="Continue" />}
    >
      <ChangeBankAccountForm />
    </FormikDrawerContent>
  );
}
